import React from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import AppFallback from '../../common/components/AppFallback/AppFallback';
import { AppRoute } from './routes.types';
import { routesByRole, publicRoutes } from './routes.config';
import { defaultRoutesByRole, roles, toRolePage } from './paths';
import { useAppRole } from './useRolePath';
import { RouteEnum } from './routeEnum';
import { MainLayout } from '../layouts';
import { useCookieSessionBootstrap } from '../../domains/auth/ui/hooks/useCookieSessionBootstrap';

const SignIn = React.lazy(
  () => import('../../domains/auth/ui/pages/SignInPage')
);
const SignUp = React.lazy(
  () => import('../../domains/auth/ui/pages/SignUpPage')
);
const ForgotPassword = React.lazy(
  () => import('../../domains/auth/ui/pages/ForgotPasswordPage')
);

function isAuthPath(pathname: string) {
  return (
    pathname.includes('login') ||
    pathname.includes('sign-in') ||
    pathname.includes('sign-up')
  );
}

const AppRouter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useAppRole();
  const homePath = defaultRoutesByRole[userRole];
  const { isBootstrapping } = useCookieSessionBootstrap();

  React.useEffect(() => {
    if (isBootstrapping) {
      return;
    }
    if (userRole !== roles.NONE) {
      if (location.pathname === '/' || isAuthPath(location.pathname)) {
        navigate(homePath, { replace: true });
        return;
      }
      if (location.pathname.startsWith(`/${userRole}`)) {
        return;
      }
      navigate(toRolePage(userRole, location.pathname), { replace: true });
      return;
    }

    if (location.pathname === '/') {
      navigate(RouteEnum.SIGN_IN, { replace: true });
      return;
    }

    const publicRoutePaths = publicRoutes.map(route => route.route);
    if (publicRoutePaths.includes(location.pathname)) return;
    if (location.pathname.indexOf('/sign-up') >= 0) return;
    if (
      [RouteEnum.SIGN_IN, RouteEnum.SIGN_UP, RouteEnum.FORGOT_PASSWORD].includes(
        location.pathname as RouteEnum
      )
    ) {
      return;
    }

    navigate(RouteEnum.SIGN_IN, { replace: true });
  }, [userRole, location.pathname, navigate, homePath, isBootstrapping]);

  if (isBootstrapping) {
    return <AppFallback />;
  }

  const availableRoutes = [
    ...publicRoutes.filter(route => !route.route.includes('/login')),
    ...(routesByRole[userRole] || []),
  ];

  const withFallback = (element: React.ReactNode) => (
    <React.Suspense fallback={<AppFallback />}>{element}</React.Suspense>
  );

  return (
    <Routes>
      <Route path="/" element={<Navigate to={homePath} replace />} />
      <Route path={RouteEnum.SIGN_IN} element={withFallback(<SignIn />)} />
      <Route path={RouteEnum.SIGN_UP} element={withFallback(<SignUp />)} />
      <Route
        path={RouteEnum.FORGOT_PASSWORD}
        element={withFallback(<ForgotPassword />)}
      />

      <Route element={<MainLayout />}>
        {availableRoutes.map((route: AppRoute) => (
          <Route
            key={route.route}
            path={route.route}
            element={withFallback(<route.element />)}
          />
        ))}
      </Route>
    </Routes>
  );
};

export default AppRouter;
